import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import eventosGanttService from '../services/eventosGanttService';
import onebaseService from '../services/onebaseService';
import './ActividadModal.css';

/**
 * Modal para crear/editar actividades (partidas) del Gantt
 */
const ActividadModal = ({ isOpen, onClose, eventoId, actividad = null, onSave }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    fecha_inicio: '',
    fecha_termino: '',
    color: '#3b82f6',
    orden: 0,
    porcentaje_avance: 0,
    presupuesto: null,
    presupuesto_costo_partida: null,
  });

  const [loading, setLoading] = useState(false);
  const [presupuestos, setPresupuestos] = useState([]);
  const [partidasPresupuesto, setPartidasPresupuesto] = useState([]);

  // Colores predefinidos para las actividades
  const COLORES = [
    '#3b82f6', // azul
    '#10b981', // verde
    '#f59e0b', // ámbar
    '#ef4444', // rojo
    '#69B32D', // púrpura
    '#ec4899', // rosa
    '#06b6d4', // cyan
    '#f97316', // naranja
  ];

  // Cargar presupuestos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPresupuestos();
    }
  }, [isOpen]);

  // Cargar partidas de presupuesto cuando se selecciona uno
  useEffect(() => {
    if (formData.presupuesto) {
      loadPartidasPresupuesto(formData.presupuesto);
    } else {
      setPartidasPresupuesto([]);
    }
  }, [formData.presupuesto]);

  useEffect(() => {
    if (actividad) {
      setFormData({
        titulo: actividad.titulo || '',
        descripcion: actividad.descripcion || '',
        fecha_inicio: actividad.fecha_inicio || '',
        fecha_termino: actividad.fecha_termino || '',
        color: actividad.color || '#3b82f6',
        orden: actividad.orden || 0,
        porcentaje_avance: actividad.porcentaje_avance || 0,
        presupuesto: actividad.presupuesto || null,
        presupuesto_costo_partida: actividad.presupuesto_costo_partida || null,
      });
    } else {
      setFormData({
        titulo: '',
        descripcion: '',
        fecha_inicio: '',
        fecha_termino: '',
        color: '#3b82f6',
        orden: 0,
        porcentaje_avance: 0,
        presupuesto: null,
        presupuesto_costo_partida: null,
      });
    }
  }, [actividad, isOpen]);

  const loadPresupuestos = async () => {
    try {
      const response = await onebaseService.presupuestoCostoService.getAll();
      if (!response || !response.data) {
        setPresupuestos([]);
        return;
      }

      const data = response.data;
      // Manejar tanto arrays como objetos con propiedad results
      let presupuestosArray = [];
      if (Array.isArray(data)) {
        presupuestosArray = data;
      } else if (data && typeof data === 'object' && Array.isArray(data.results)) {
        presupuestosArray = data.results;
      }
      setPresupuestos(presupuestosArray);
    } catch (error) {
      console.error('Error al cargar presupuestos:', error);
      setPresupuestos([]);
      // Mostrar toast solo si es un error importante
      if (error.response?.status !== 404) {
        toast.error('Error al cargar los presupuestos');
      }
    }
  };

  const loadPartidasPresupuesto = async (presupuestoId) => {
    try {
      const response = await onebaseService.presupuestoCostoService.getPartidas(presupuestoId);
      if (!response || !response.data) {
        setPartidasPresupuesto([]);
        return;
      }

      const data = response.data;
      // Manejar tanto arrays como objetos con propiedad results
      let partidasArray = [];
      if (Array.isArray(data)) {
        partidasArray = data;
      } else if (data && typeof data === 'object' && Array.isArray(data.results)) {
        partidasArray = data.results;
      }
      setPartidasPresupuesto(partidasArray);
    } catch (error) {
      console.error('Error al cargar partidas de presupuesto:', error);
      setPartidasPresupuesto([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'orden' || name === 'porcentaje_avance') {
      newValue = parseFloat(value);
    } else if (name === 'presupuesto' || name === 'presupuesto_costo_partida') {
      // Convertir string vacío a null, números a parseInt
      newValue = value === '' ? null : parseInt(value, 10);
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Si se cambia el presupuesto, limpiar presupuesto_costo_partida
    if (name === 'presupuesto') {
      setFormData(prev => ({
        ...prev,
        presupuesto_costo_partida: null
      }));
    }
  };

  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  const validateForm = () => {
    if (!formData.titulo.trim()) {
      toast.error('El título es requerido');
      return false;
    }

    if (!formData.fecha_inicio) {
      toast.error('La fecha de inicio es requerida');
      return false;
    }

    if (!formData.fecha_termino) {
      toast.error('La fecha de término es requerida');
      return false;
    }

    if (new Date(formData.fecha_termino) < new Date(formData.fecha_inicio)) {
      toast.error('La fecha de término debe ser igual o posterior a la fecha de inicio');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSend = {
        evento: eventoId,
        ...formData,
      };

      if (actividad) {
        await eventosGanttService.actividades.update(actividad.id, dataToSend);
        toast.success('Actividad actualizada correctamente');
      } else {
        await eventosGanttService.actividades.create(dataToSend);
        toast.success('Actividad creada correctamente');
      }

      // Disparar evento personalizado para refrescar el gantt
      window.dispatchEvent(new CustomEvent('actividadActualizada'));
      onSave();
      onClose();
    } catch (error) {
      console.error('Error al guardar actividad:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la actividad');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content actividad-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>
            {actividad ? '✏️ Editar Actividad' : '➕ Nueva Actividad'}
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Título */}
          <div className="form-group">
            <label htmlFor="titulo">Título de la Actividad *</label>
            <input
              id="titulo"
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              placeholder="Ej: Diseño de campaña"
              maxLength={200}
            />
            <small>{formData.titulo.length}/200</small>
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción opcional de la actividad"
              rows={3}
            />
          </div>

          {/* Fechas */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fecha_inicio">Fecha de Inicio *</label>
              <input
                id="fecha_inicio"
                type="date"
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="fecha_termino">Fecha de Término *</label>
              <input
                id="fecha_termino"
                type="date"
                name="fecha_termino"
                value={formData.fecha_termino}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Cantidad de Días */}
          {formData.fecha_inicio && formData.fecha_termino && (
            <div className="info-box">
              <span className="info-label">Duración:</span>
              <span className="info-value">
                {Math.ceil((new Date(formData.fecha_termino) - new Date(formData.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1} días
              </span>
            </div>
          )}

          {/* Presupuesto */}
          <div className="form-group">
            <label htmlFor="presupuesto">Presupuesto de Costo</label>
            <select
              id="presupuesto"
              name="presupuesto"
              value={formData.presupuesto || ''}
              onChange={handleChange}
            >
              <option value="">-- Seleccionar Presupuesto --</option>
              {Array.isArray(presupuestos) && presupuestos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nombre || `Presupuesto #${p.id}`}
                </option>
              ))}
            </select>
            <small>Opcional - Presupuesto de costo asociado a esta actividad</small>
          </div>

          {/* Partida de Presupuesto */}
          {formData.presupuesto && (
            <div className="form-group">
              <label htmlFor="presupuesto_costo_partida">Partida del Presupuesto</label>
              <select
                id="presupuesto_costo_partida"
                name="presupuesto_costo_partida"
                value={formData.presupuesto_costo_partida || ''}
                onChange={handleChange}
              >
                <option value="">-- Seleccionar Partida --</option>
                {Array.isArray(partidasPresupuesto) && partidasPresupuesto.map(partida => (
                  <option key={partida.id} value={partida.id}>
                    {partida.nombre || `Partida #${partida.id}`}
                  </option>
                ))}
              </select>
              <small>Opcional - Partida específica del presupuesto</small>
            </div>
          )}

          {/* Color */}
          <div className="form-group">
            <label>Color para el Gantt</label>
            <div className="color-picker">
              {COLORES.map(color => (
                <div
                  key={color}
                  className={`color-option ${formData.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* Orden */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="orden">Orden de Visualización</label>
              <input
                id="orden"
                type="number"
                name="orden"
                value={formData.orden}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="porcentaje_avance">Porcentaje de Avance (%)</label>
              <input
                id="porcentaje_avance"
                type="number"
                name="porcentaje_avance"
                value={formData.porcentaje_avance}
                onChange={handleChange}
                min="0"
                max="100"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActividadModal;
